import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Building2,
  Database,
  KeyRound,
  Server,
  Settings2,
  ShieldCheck,
  Users
} from 'lucide-react';
import AppLayout from './Layout';
import { useOrganization } from '../context/OrganizationContext';

const cards = [
  {
    title: 'Members & roles',
    description:
      'Organization-scoped member management will be enabled after the tenant membership APIs are added.',
    icon: Users,
    status: 'Planned'
  },
  {
    title: 'Gateway fleet',
    description:
      'Manage enrolled gateways, sites, network segments, health, and response capability.',
    icon: Server,
    status: 'Available',
    to: '/gateways'
  },
  {
    title: 'Agent enrollment',
    description:
      'Create and review organization-scoped single-use gateway enrollment tokens.',
    icon: KeyRound,
    status: 'Available',
    to: '/gateways'
  },
  {
    title: 'Ingestion activity',
    description:
      'Review ingestion jobs owned by the active organization.',
    icon: Database,
    status: 'Available',
    to: '/ingestions'
  },
  {
    title: 'Organization settings',
    description:
      'Workspace-level configuration will be enabled as tenant-safe settings APIs become available.',
    icon: Settings2,
    status: 'Planned'
  },
  {
    title: 'Security policy',
    description:
      'Tenant policy controls remain disabled until enforcement ownership is organization-aware.',
    icon: ShieldCheck,
    status: 'Planned'
  }
];

export default function OrganizationAdminPage({ onLogout }) {
  const {
    activeOrganization,
    organizationLoading,
    organizationError
  } = useOrganization();

  if (!organizationLoading && activeOrganization?.membership_role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AppLayout onLogout={onLogout}>
      <div className="space-y-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-700">
                  Organization administration
                </div>
                <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                  {organizationLoading
                    ? 'Loading workspace...'
                    : activeOrganization?.name || 'Workspace unavailable'}
                </h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                  Manage customer-owned resources for the selected organization. Platform-wide CybeDefender controls remain isolated in the internal Ops environment.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Access scope
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                Organization admin
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                Active workspace only
              </div>
            </div>
          </div>

          {organizationError && !organizationLoading && (
            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {organizationError}
            </div>
          )}
        </section>

        <section>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Administration</h3>
            <p className="mt-1 text-sm text-slate-500">
              Only tenant-safe capabilities are enabled here. Planned controls remain read-only until their ownership boundaries are implemented.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {cards.map((card) => {
              const Icon = card.icon;
              const available = card.status === 'Available';
              const content = (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span
                      className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                        available
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {card.status}
                    </span>
                  </div>
                  <h4 className="mt-4 text-base font-semibold text-slate-900">
                    {card.title}
                  </h4>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    {card.description}
                  </p>
                  {available && (
                    <div className="mt-4 text-sm font-semibold text-cyan-700">
                      Open →
                    </div>
                  )}
                </>
              );

              if (available && card.to) {
                return (
                  <Link
                    key={card.title}
                    to={card.to}
                    className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-200 hover:shadow-md"
                  >
                    {content}
                  </Link>
                );
              }

              return (
                <div
                  key={card.title}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  {content}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </AppLayout>
  );
}
